import { useTranslations } from "@acto/web-common/locales";
import { styled } from "@pigment-css/react";
import * as RadixDropdown from "@radix-ui/react-dropdown-menu";
import { getPeriodDateContext } from "../../utils/periodToDateRange";
import { PeriodOption } from "./types";

interface DateRangeSelectorDropdownProps {
  selectedPeriod: PeriodOption;
  onPeriodSelect: (period: PeriodOption) => void;
  onSelectRange: () => void;
  excludedPeriods?: PeriodOption[];
}

export const DropdownContent = styled(RadixDropdown.Content)(({ theme }) => ({
  minWidth: "220px",
  backgroundColor: theme.managementView.colors.neutral[0],
  border: `1px solid ${theme.managementView.colors.neutral[8]}`,
  borderRadius: theme.managementView.radius[16],
  boxShadow: theme.shadows[2],
  padding: theme.managementView.spacing[8],
  zIndex: 10010,
  marginTop: theme.managementView.spacing[4],
}));

const SectionLabel = styled("div")(({ theme }) => ({
  padding: `${theme.managementView.spacing[8]} ${theme.managementView.spacing[18]}`,
  fontSize: theme.managementView.font.size[13],
  fontWeight: theme.managementView.font.weight[500],
  fontFamily: theme.managementView.font.family,
  color: theme.managementView.colors.neutral[56],
  textTransform: "uppercase",
  letterSpacing: "0.05em",
}));

export const MenuItem = styled(RadixDropdown.Item)<{ "data-selected"?: boolean }>(({ theme }) => ({
  color: theme.managementView.colors.neutral[100],
  padding: `${theme.managementView.spacing[8]} ${theme.managementView.spacing[18]}`,
  borderRadius: theme.managementView.radius[16],
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.managementView.spacing[16],
  cursor: "pointer",
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[440],
  fontFamily: theme.managementView.font.family,
  backgroundColor: "transparent",
  border: "none",
  outline: "none",
  "&:hover": {
    backgroundColor: theme.managementView.colors.neutral[4],
  },
  "&:focus": {
    backgroundColor: theme.managementView.colors.neutral[4],
  },
  '&[data-selected="true"]': {
    backgroundColor: theme.managementView.colors.neutral[8],
  },
}));

const DateContext = styled("span")(({ theme }) => ({
  color: theme.managementView.colors.neutral[56],
  fontSize: theme.managementView.font.size[13],
  fontWeight: theme.managementView.font.weight[400],
}));

const Separator = styled(RadixDropdown.Separator)(({ theme }) => ({
  height: "1px",
  backgroundColor: theme.managementView.colors.neutral[8],
  margin: `${theme.managementView.spacing[8]} 0`,
}));

interface PeriodItemProps {
  period: PeriodOption;
  label: string;
  isSelected: boolean;
  onSelect: (period: PeriodOption) => void;
  showDateContext?: boolean;
}

const PeriodItem = ({ period, label, isSelected, onSelect, showDateContext = false }: PeriodItemProps) => {
  const dateContext = showDateContext ? getPeriodDateContext(period) : "";

  return (
    <MenuItem data-selected={isSelected} onSelect={() => onSelect(period)}>
      <span>{label}</span>
      {dateContext && <DateContext>{dateContext}</DateContext>}
    </MenuItem>
  );
};

export const DateRangeSelectorDropdown = ({
  selectedPeriod,
  onPeriodSelect,
  onSelectRange,
  excludedPeriods,
}: DateRangeSelectorDropdownProps) => {
  const t = useTranslations("dateRange");

  const isEnabled = (period: PeriodOption) => !excludedPeriods?.includes(period);

  const showCustomRange = isEnabled(PeriodOption.CUSTOM);
  const showThisWeek = isEnabled(PeriodOption.THIS_WEEK);
  const showThisMonth = isEnabled(PeriodOption.THIS_MONTH);
  const showNowSection = showThisWeek || showThisMonth;

  return (
    <DropdownContent align="end">
      {showCustomRange && (
        <>
          <MenuItem onSelect={onSelectRange}>{t("selectRange")}</MenuItem>
          <Separator />
        </>
      )}

      {showNowSection && (
        <>
          <SectionLabel>{t("now")}</SectionLabel>
          {showThisWeek && (
            <PeriodItem
              period={PeriodOption.THIS_WEEK}
              label={t("thisWeek")}
              isSelected={selectedPeriod === PeriodOption.THIS_WEEK}
              onSelect={onPeriodSelect}
            />
          )}
          {showThisMonth && (
            <PeriodItem
              period={PeriodOption.THIS_MONTH}
              label={t("thisMonth")}
              isSelected={selectedPeriod === PeriodOption.THIS_MONTH}
              onSelect={onPeriodSelect}
            />
          )}
          <Separator />
        </>
      )}

      <SectionLabel>{t("past")}</SectionLabel>
      {isEnabled(PeriodOption.LAST_MONTH) && (
        <PeriodItem
          period={PeriodOption.LAST_MONTH}
          label={t("lastMonth")}
          isSelected={selectedPeriod === PeriodOption.LAST_MONTH}
          onSelect={onPeriodSelect}
          showDateContext
        />
      )}
      {isEnabled(PeriodOption.LAST_3_MONTHS) && (
        <PeriodItem
          period={PeriodOption.LAST_3_MONTHS}
          label={t("last3Months")}
          isSelected={selectedPeriod === PeriodOption.LAST_3_MONTHS}
          onSelect={onPeriodSelect}
          showDateContext
        />
      )}
      {isEnabled(PeriodOption.LAST_6_MONTHS) && (
        <PeriodItem
          period={PeriodOption.LAST_6_MONTHS}
          label={t("last6Months")}
          isSelected={selectedPeriod === PeriodOption.LAST_6_MONTHS}
          onSelect={onPeriodSelect}
          showDateContext
        />
      )}
      {isEnabled(PeriodOption.LAST_12_MONTHS) && (
        <PeriodItem
          period={PeriodOption.LAST_12_MONTHS}
          label={t("last12Months")}
          isSelected={selectedPeriod === PeriodOption.LAST_12_MONTHS}
          onSelect={onPeriodSelect}
          showDateContext
        />
      )}
    </DropdownContent>
  );
};
